import type { BillingProcess } from '@policyvision/contracts';
import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Label } from '@/common/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/common/components/ui/select';

import type { BillingSummary } from '../api/FetchBillingList';
import { NamingRuleForm } from './NamingRuleForm';

type RenameDocumentFormProps = {
  billing: BillingSummary;
  process: BillingProcess;
  onCancel: () => void;
};

export const RenameDocumentForm = ({
  billing,
  process,
  onCancel,
}: RenameDocumentFormProps) => {
  const { t, i18n } = useTranslation();
  const documentTypeSelectId = useId();
  const [documentTypeId, setDocumentTypeId] = useState<number>();

  const orderedDocumentTypes = [...process.documentTypes].sort((a, b) =>
    a.label.localeCompare(b.label, i18n.language),
  );

  const documentType = orderedDocumentTypes.find(
    ({ id }) => id === documentTypeId,
  );

  return (
    <div className="flex flex-col gap-3 border-t pt-3">
      <div className="flex flex-col gap-1.5">
        <Label
          htmlFor={documentTypeSelectId}
          className="text-sm font-medium text-muted-foreground"
        >
          {t('billing.drawer.rename.documentType')}
        </Label>
        <Select
          value={documentTypeId ? String(documentTypeId) : ''}
          onValueChange={(id) => setDocumentTypeId(Number(id))}
        >
          <SelectTrigger
            id={documentTypeSelectId}
            className="w-full bg-background"
          >
            <SelectValue
              placeholder={t('billing.drawer.rename.documentTypePlaceholder')}
            />
          </SelectTrigger>
          <SelectContent>
            {orderedDocumentTypes.map(({ id, label }) => (
              <SelectItem key={id} value={String(id)}>
                {label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {documentType && (
          <p className="text-xs text-muted-foreground">
            {t('billing.drawer.rename.rule')}: {documentType.namingRule}
          </p>
        )}
      </div>

      <NamingRuleForm
        key={documentType?.id}
        billing={billing}
        process={process}
        documentType={documentType}
        onCancel={onCancel}
      />
    </div>
  );
};
